import React, { useState, useEffect } from 'react';
import { posAPI } from '../services/api';
import GlobalConfig from '../config/global';
import './ProductSearch.css';

const ProductSearch = ({ onProductSelect, order }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('text'); // 'text' or 'image'

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    try {
      if (searchTerm.trim() === '') {
        setFilteredProducts(products);
      } else {
        const filtered = products.filter(product => {
          const name = product?.name || '';
          const brand = product?.brand || '';
          const sku = product?.sku || '';
          const search = searchTerm.toLowerCase();

          return name.toLowerCase().includes(search) || 
                 brand.toLowerCase().includes(search) || 
                 sku.toLowerCase().includes(search);
        });
        setFilteredProducts(filtered);
      }
      setError(null);
    } catch (err) {
      console.error('Error filtering products:', err);
      setError('Error filtering products');
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await posAPI.getProducts();
      if (response.data && Array.isArray(response.data.data)) {
        const cleanedProducts = response.data.data.map(product => ({
          ...product,
          name: product.name || 'Unnamed Product',
          brand: product.brand || 'No Brand',
          sku: product.sku || 'N/A',
          price: product.price || 0,
          sale_price: product.sale_price || 0,
          stock_quantity: product.stock_quantity || 0,
          low_stock_threshold: product.low_stock_threshold || 0,
          variants: product.variants || [],
          images: product.images || []
        }));
        setProducts(cleanedProducts);
        setFilteredProducts(cleanedProducts);
      } else {
        setError('Invalid response format from server');
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Failed to load products');
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableStockForProduct = (product) => {
    if (!product) return 0;
    
    // If product has variants, calculate total available stock
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((total, variant) => {
        return total + (variant.stock_quantity || 0);
      }, 0);
    }
    
    // For regular products, return product stock
    return product.stock_quantity || 0;
  };

  const getOrderQuantityForProduct = (product) => {
    if (!order || order.length === 0) return 0;
    
    // For products with variants, sum quantities of all variants
    if (product.variants && product.variants.length > 0) {
      return order.reduce((total, orderItem) => {
        if (orderItem.base_product_id === product.id) {
          return total + orderItem.quantity;
        }
        return total;
      }, 0);
    }
    
    // For regular products, find the exact product in order
    const orderItem = order.find(item => item.id === product.id);
    return orderItem ? orderItem.quantity : 0;
  };

  const handleProductClick = (product) => {
    if (!product) return;
    
    // Check if product is out of stock
    const availableStock = getAvailableStockForProduct(product);
    if (availableStock <= 0) {
      setError('This product is out of stock');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Check if we've already reached the maximum stock in the order
    const currentOrderQuantity = getOrderQuantityForProduct(product);
    if (currentOrderQuantity >= availableStock) {
      setError(`Maximum available stock (${availableStock}) already in order`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
    } else {
      onProductSelect(product);
    }
  };

  const handleVariantSelect = (variant) => {
    if (!selectedProduct || !variant) return;
    
    // Check if variant is out of stock
    if ((variant.stock_quantity || 0) <= 0) {
      setError('This variant is out of stock');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Check if we've already reached the maximum stock for this variant in the order
    const currentVariantQuantity = order.reduce((total, orderItem) => {
      if (orderItem.variant && (orderItem.variant.id === variant.id || orderItem.variant.variant_id === variant.variant_id)) {
        return total + orderItem.quantity;
      }
      return total;
    }, 0);

    if (currentVariantQuantity >= (variant.stock_quantity || 0)) {
      setError(`Maximum available stock (${variant.stock_quantity || 0}) for this variant already in order`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    const productWithVariant = {
      ...selectedProduct,
      id: variant.id || variant.variant_id,
      name: `${selectedProduct.name || 'Product'} - ${variant.variant_name || 'Variant'}`,
      price: parseFloat(variant.sale_price || variant.price || 0),
      base_product_id: selectedProduct.id,
      variant: variant,
      stock_quantity: variant.stock_quantity || 0
    };
    onProductSelect(productWithVariant);
    setSelectedProduct(null);
  };

  const getCurrentPrice = (product) => {
    if (!product) return 0;
    
    try {
      const now = new Date();
      const saleStart = product.sale_start_date ? new Date(product.sale_start_date) : null;
      const saleEnd = product.sale_end_date ? new Date(product.sale_end_date) : null;
      
      if (product.sale_price && saleStart && saleEnd && now >= saleStart && now <= saleEnd) {
        return parseFloat(product.sale_price) || 0;
      }
      return parseFloat(product.price) || 0;
    } catch (error) {
      console.error('Error calculating price:', error);
      return parseFloat(product.price) || 0;
    }
  };

  const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return `LKR ${numericAmount.toLocaleString('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getProductImage = (product) => {
    if (!product.images || product.images.length === 0) {
      return '/images/placeholder-product.png'; // Fallback placeholder image
    }
    
    // Find primary image or use first image
    const primaryImage = product.images.find(img => img.is_primary) || product.images[0];
    
    if (primaryImage && primaryImage.image_path) {
      // Construct full image URL using GlobalConfig
      const baseUrl = GlobalConfig.API_BASE_URL.replace(/\/$/, ''); // Remove trailing slash if exists
      const imagePath = primaryImage.image_path.replace(/^\//, ''); // Remove leading slash if exists
      return `${baseUrl}/storage/${imagePath}`;
    }
    
    return '/images/placeholder-product.png';
  };

  const renderTextView = () => (
    <div className="products-grid text-view">
      {filteredProducts.map(product => {
        const availableStock = getAvailableStockForProduct(product);
        const currentOrderQuantity = getOrderQuantityForProduct(product);
        const canAddMore = currentOrderQuantity < availableStock;
        
        return (
          <div
            key={product.id}
            className={`product-card ${availableStock <= 0 ? 'out-of-stock' : ''} ${!canAddMore ? 'max-in-order' : ''}`}
            onClick={() => canAddMore && availableStock > 0 ? handleProductClick(product) : null}
          >
            <div className="product-name">{product?.name || 'Unnamed Product'}</div>
            <div className="product-brand">{product?.brand || 'No Brand'}</div>
            <div className="product-sku">SKU: {product?.sku || 'N/A'}</div>
            <div className="product-price">{formatCurrency(getCurrentPrice(product))}</div>
            <div className="product-stock">
              Stock: {availableStock}
              {currentOrderQuantity > 0 && (
                <span className="in-order-count"> (In order: {currentOrderQuantity})</span>
              )}
              {(availableStock <= (product?.low_stock_threshold || 0)) && (
                <span className="low-stock-warning"> ⚠</span>
              )}
            </div>
            {!canAddMore && availableStock > 0 && (
              <div className="max-order-warning">Max in order</div>
            )}
            {product?.variants && product.variants.length > 0 && (
              <div className="variants-indicator">
                {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderImageView = () => (
    <div className="products-grid image-view">
      {filteredProducts.map(product => {
        const availableStock = getAvailableStockForProduct(product);
        const currentOrderQuantity = getOrderQuantityForProduct(product);
        const canAddMore = currentOrderQuantity < availableStock;
        const productImage = getProductImage(product);
        
        return (
          <div
            key={product.id}
            className={`product-card image-card ${availableStock <= 0 ? 'out-of-stock' : ''} ${!canAddMore ? 'max-in-order' : ''}`}
            onClick={() => canAddMore && availableStock > 0 ? handleProductClick(product) : null}
          >
            <div className="product-image-container">
              <img 
                src={productImage} 
                alt={product?.name || 'Product'}
                className="product-image"
                onError={(e) => {
                  e.target.src = '/images/placeholder-product.png';
                }}
              />
              {product?.variants && product.variants.length > 0 && (
                <div className="variants-indicator">
                  {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}
                </div>
              )}
              {availableStock <= 0 && (
                <div className="out-of-stock-overlay">Out of Stock</div>
              )}
              {!canAddMore && availableStock > 0 && (
                <div className="max-order-overlay">Max in Order</div>
              )}
            </div>
            <div className="product-info">
              <div className="product-name">{product?.name || 'Unnamed Product'}</div>
              <div className="product-price">{formatCurrency(getCurrentPrice(product))}</div>
              <div className="product-stock-info">
                <span className="stock-count">Stock: {availableStock}</span>
                {currentOrderQuantity > 0 && (
                  <span className="in-order-count">({currentOrderQuantity} in order)</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="product-search">
      <div className="search-header">
        <h2>Products</h2>
        <div className="header-controls">
          <div className="view-mode-toggle">
            <button 
              className={`view-btn ${viewMode === 'text' ? 'active' : ''}`}
              onClick={() => setViewMode('text')}
            >
              Text View
            </button>
            <button 
              className={`view-btn ${viewMode === 'image' ? 'active' : ''}`}
              onClick={() => setViewMode('image')}
            >
              Image View
            </button>
          </div>
          <button className="refresh-btn" onClick={loadProducts} disabled={loading}>
            {loading ? '⟳' : '↻'}
          </button>
        </div>
      </div>
      
      <div className="search-box">
        <input
          type="text"
          placeholder="Search by name, brand, or SKU..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}
      
      {loading ? (
        <div className="loading-products">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="no-products-found">
          {searchTerm ? `No products found for "${searchTerm}"` : 'No products available'}
        </div>
      ) : (
        viewMode === 'text' ? renderTextView() : renderImageView()
      )}

      {/* Variant Selection Modal */}
      {selectedProduct && (
        <div className="modal-overlay">
          <div className="variant-modal">
            <div className="modal-header">
              <h3>Select Variant - {selectedProduct?.name || 'Product'}</h3>
              <button 
                className="close-modal"
                onClick={() => setSelectedProduct(null)}
              >
                ×
              </button>
            </div>
            <div className="variants-list">
              {selectedProduct?.variants && selectedProduct.variants.length > 0 ? (
                selectedProduct.variants.map(variant => {
                  const currentVariantQuantity = order.reduce((total, orderItem) => {
                    if (orderItem.variant && (orderItem.variant.id === variant.id || orderItem.variant.variant_id === variant.variant_id)) {
                      return total + orderItem.quantity;
                    }
                    return total;
                  }, 0);
                  const canAddVariant = currentVariantQuantity < (variant.stock_quantity || 0);
                  
                  return (
                    <div
                      key={variant.id || variant.variant_id}
                      className={`variant-item ${(variant.stock_quantity || 0) <= 0 ? 'out-of-stock' : ''} ${!canAddVariant ? 'max-in-order' : ''}`}
                      onClick={() => canAddVariant && (variant.stock_quantity || 0) > 0 ? handleVariantSelect(variant) : null}
                    >
                      <div className="variant-name">{variant?.variant_name || 'Unnamed Variant'}</div>
                      <div className="variant-price">
                        {formatCurrency(variant.sale_price || variant.price)}
                      </div>
                      <div className="variant-stock">
                        Stock: {variant?.stock_quantity || 0}
                        {currentVariantQuantity > 0 && (
                          <span className="in-order-count"> (In order: {currentVariantQuantity})</span>
                        )}
                        {(variant?.stock_quantity || 0) <= (variant?.low_stock_threshold || 0) && (
                          <span className="low-stock-warning"> ⚠</span>
                        )}
                      </div>
                      {!canAddVariant && (variant.stock_quantity || 0) > 0 && (
                        <div className="max-order-warning">Max in order</div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="no-variants">No variants available</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSearch;